import * as React from 'react';
import type {
  Business,
  DueDiligenceDocument,
  MUO,
  OnboardingDocumentRequirement,
  RequirementReference
} from '@gcv/shared';
import { RequirementDataSource } from '@gcv/shared';
import { Box, Grid } from '@mui/material';

import { Container, ContainerItem, DrawerInfoItem, Text } from 'ui';
import { DateTimeHelpers } from 'util/dateTime.util';

interface Props extends Record<string, unknown> {
  dueDiligenceTemplate: OnboardingDocumentRequirement & RequirementReference;
  documentMetadata: DueDiligenceDocument;
  currentDispensary: Business | MUO;
}

export const DocsDrawerLifecycleValues: React.FC<Props> = (props) => {
  const { dueDiligenceTemplate, documentMetadata, currentDispensary } = props;

  const isMsbRequirement = dueDiligenceTemplate.data_source === RequirementDataSource.FincenMsb;

  const expirationDate = documentMetadata.lifecycle_values?.expiration_date
    ? DateTimeHelpers.formatISOToFriendlyDateString(
        documentMetadata.lifecycle_values.expiration_date.value,
        currentDispensary.iana_timezone
      )
    : undefined;
  /**
   * In the case a user manually uploads an msb reg, and later gets matched, check the calculated expiration date
   * against the user entered expiration date. Show both if there is a difference
   */
  const msbCalculatedExpiration =
    isMsbRequirement && documentMetadata.msb_registration
      ? DateTimeHelpers.formatISOToFriendlyDateString(
          DateTimeHelpers.parseFromISOString(
            documentMetadata.msb_registration.auth_sign_date,
            currentDispensary.iana_timezone
          )
            .plus({ year: 1 })
            .endOf('year')
            .toISODate(),
          currentDispensary.iana_timezone
        )
      : undefined;

  const msbExpirationTooltip = isMsbRequirement
    ? "This date is based on FinCEN's requirement that registrations must be renewed by the end of the second calendar year."
    : undefined;

  return (
    <Container width="100%" margin="1rem 0" padding="0">
      <Box
        sx={{
          backgroundColor: 'rgb(232 244 238)',
          borderRadius: '8px',
          padding: '1rem',
          width: '100%',
          border: '1px solid #C6CCDA'
        }}
      >
        <ContainerItem width="100%" margin="0" padding="0">
          <Text
            sx={{ fontWeight: 700 }}
            content={isMsbRequirement ? 'Registration Information' : 'Lifecycle Information'}
          />
        </ContainerItem>
        <ContainerItem width="100%" margin="0" padding="0">
          <Container width="100%" margin="1rem 0" padding="0">
            <Grid container rowSpacing={2} columnSpacing={2}>
              {dueDiligenceTemplate.lifecycle_config?.expiration_date?.enabled &&
              msbCalculatedExpiration &&
              expirationDate !== msbCalculatedExpiration ? (
                <>
                  <DrawerInfoItem
                    label={dueDiligenceTemplate.lifecycle_config.expiration_date.display_label}
                    value={msbCalculatedExpiration}
                    tooltip={msbExpirationTooltip}
                  />
                  <DrawerInfoItem label={'User-Entered Expiration Date'} value={expirationDate} />
                </>
              ) : (
                dueDiligenceTemplate.lifecycle_config?.expiration_date?.enabled && (
                  <DrawerInfoItem
                    label={dueDiligenceTemplate.lifecycle_config.expiration_date.display_label}
                    value={expirationDate}
                    tooltip={msbExpirationTooltip}
                  />
                )
              )}
              {dueDiligenceTemplate.lifecycle_config?.identifier?.enabled && (
                <DrawerInfoItem
                  label={dueDiligenceTemplate.lifecycle_config.identifier.display_label}
                  value={documentMetadata?.lifecycle_values?.identifier?.value}
                />
              )}
              {dueDiligenceTemplate.lifecycle_config?.text?.enabled && (
                <DrawerInfoItem
                  label={dueDiligenceTemplate.lifecycle_config.text.display_label}
                  value={documentMetadata?.lifecycle_values?.text?.value}
                />
              )}
              {dueDiligenceTemplate.lifecycle_config?.select?.enabled && (
                <DrawerInfoItem
                  label={dueDiligenceTemplate.lifecycle_config.select.display_label}
                  value={documentMetadata?.lifecycle_values?.select?.value}
                />
              )}
              {isMsbRequirement && documentMetadata?.msb_registration && (
                <>
                  <DrawerInfoItem
                    label={'Legal Name'}
                    value={documentMetadata?.msb_registration?.legal_name}
                  />
                  <DrawerInfoItem
                    label={'Signed Date'}
                    value={
                      documentMetadata?.msb_registration?.auth_sign_date
                        ? DateTimeHelpers.formatISOToFriendlyDateString(
                            documentMetadata?.msb_registration?.auth_sign_date,
                            currentDispensary.iana_timezone
                          )
                        : '--'
                    }
                  />
                  <DrawerInfoItem label={'Status'} value={documentMetadata?.msb_registration?.status} />
                  <DrawerInfoItem
                    label={'MSB Activities'}
                    value={documentMetadata?.msb_registration?.msb_activities.join(', ')}
                  />
                  <DrawerInfoItem
                    label={'States Registered'}
                    value={documentMetadata?.msb_registration?.states_of_msb_activities.join(', ')}
                  />
                </>
              )}
            </Grid>
          </Container>
        </ContainerItem>
      </Box>
    </Container>
  );
};
